import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SourceAttribution } from "@/lib/source-attribution";
import { invalidateAttentionInbox } from "./attention-cache";
import {
	API_URL,
	type ApiOrgContext,
	fetchWithTimeout,
	resolveApiScope,
} from "./core";

export interface BrowserHandoff {
	run_id: number | null;
	state: "ready" | "preparing" | "expired" | "completed";
	expires_at: string | null;
	error_message: string | null;
}

export interface NotificationItem extends SourceAttribution {
	id: number;
	organization_id: string;
	user_id: string;
	type: string;
	title: string;
	body: string | null;
	resource_type: string | null;
	resource_id: string | null;
	resource_url: string | null;
	browser_url?: string | null;
	browser_handoff?: BrowserHandoff | null;
	is_read: boolean;
	created_at: string;
	/** Source attribution for the underlying event (mirrors ContentItem). */
}

function notificationsUrl(orgContext: ApiOrgContext | string, path = "") {
	const scope = resolveApiScope(orgContext);
	return `${API_URL}/api/${scope.slug}/notifications${path}`;
}

function requireNotificationOrgContext(
	orgContext?: ApiOrgContext | string | null,
): ApiOrgContext | string {
	if (!orgContext) throw new Error("Workspace context is required");
	return orgContext;
}

export function useMarkNotificationRead(
	orgContext?: ApiOrgContext | string | null,
) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (notificationId: number) => {
			const res = await fetchWithTimeout(
				notificationsUrl(
					requireNotificationOrgContext(orgContext),
					`/${notificationId}/read`,
				),
				{
					method: "PATCH",
					credentials: "include",
				},
			);
			if (!res.ok) throw new Error("Failed to mark as read");
		},
		onSettled: () => invalidateAttentionInbox(queryClient),
	});
}

/**
 * Delete a notification from the current user's inbox. "Deleting" is per-user:
 * the event stays in the org-wide stream; only the target row is dropped
 * (notifications/service.ts deleteNotification). Used as the "Done" action on
 * browser-handoff cards, so a handled draft leaves the lens while its event
 * remains addressable.
 */
export function useDeleteNotification(
	orgContext?: ApiOrgContext | string | null,
) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (notificationId: number) => {
			const res = await fetchWithTimeout(
				notificationsUrl(
					requireNotificationOrgContext(orgContext),
					`/${notificationId}`,
				),
				{ method: "DELETE", credentials: "include" },
			);
			if (!res.ok) throw new Error("Failed to delete notification");
		},
		onSettled: () => {
			invalidateAttentionInbox(queryClient);
		},
	});
}

/** Recreate an expired page-activated draft, preserving its exact saved input. */
export function useRecreateBrowserHandoff(
	orgContext?: ApiOrgContext | string | null,
) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (notificationId: number) => {
			const res = await fetchWithTimeout(
				notificationsUrl(
					requireNotificationOrgContext(orgContext),
					`/${notificationId}/browser-handoff/recreate`,
				),
				{ method: "POST", credentials: "include" },
			);
			const data = (await res.json().catch(() => null)) as
				| { browser_url: string; browser_handoff: BrowserHandoff }
				| { error?: string }
				| null;
			if (!res.ok || !data || !("browser_handoff" in data)) {
				throw new Error(
					(data && "error" in data && data.error) ||
						"Failed to recreate browser draft",
				);
			}
			return data;
		},
		onSettled: () => invalidateAttentionInbox(queryClient),
	});
}

/**
 * Mark an exact, reviewed notification set as read. The REST API currently has
 * one-row and whole-inbox mutations; composing the one-row mutation here keeps
 * "visible" bounded to the IDs the UI actually showed instead of silently
 * widening to every notification in the workspace.
 */
export function useMarkNotificationsRead(
	orgContext?: ApiOrgContext | string | null,
) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (notificationIds: number[]) => {
			const ids = [...new Set(notificationIds)];
			const results = await Promise.allSettled(
				ids.map(async (notificationId) => {
					const res = await fetchWithTimeout(
						notificationsUrl(
							requireNotificationOrgContext(orgContext),
							`/${notificationId}/read`,
						),
						{ method: "PATCH", credentials: "include" },
					);
					if (!res.ok)
						throw new Error(
							`Failed to mark notification ${notificationId} as read`,
						);
					return notificationId;
				}),
			);
			const failedIds = results.flatMap((result, index) => {
				const id = ids[index];
				return result.status === "rejected" && id !== undefined ? [id] : [];
			});
			return {
				succeeded: ids.length - failedIds.length,
				failed: failedIds.length,
				failedIds,
			};
		},
		onSettled: () => {
			invalidateAttentionInbox(queryClient);
		},
	});
}
